import moment from 'moment';
import PropTypes from 'prop-types';
import * as React from 'react';
import { useSelector } from 'react-redux';

import {
  AccessAlarm,
  CheckBox,
  CheckBoxOutlineBlank,
  DoNotDisturb,
  Schedule,
  Task,
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  ListItem,
  ListItemAvatar,
  ListItemText,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import Horse from '../../../../../../components/Horse';
import { primary } from '../../../../../../utils/brandColors';
import { getScheduleSeriesText } from '../../../../../../utils/scheduleHelper';
import TagReminderModal from './components/TagReminderModal';
import { getChipForStatus } from './statusChip';

export { getChipForStatus } from './statusChip';

const cellStyle = {
  borderRight: 1,
  borderColor: 'divider',
  maxWidth: 200,
  minWidth: 200,
};

export const TagReminderStatus = ({
  tagReminder,
  center,
}) => {
  let description = 'Add a previous record to calculate the reminder date.';
  if (tagReminder.status === 'Incomplete') {
    const eventDate = tagReminder.lastEvent && (tagReminder.lastEvent.completeDate || tagReminder.lastEvent.startDate);
    description = eventDate
      ? `Since ${moment(eventDate).format('ll')}`
      : 'An active task is incomplete';
  }
  if (tagReminder.status === 'Unscheduled') {
    description = tagReminder.dueDate
      ? `Schedule by ${moment(tagReminder.dueDate).format('ll')}`
      : 'Schedule the next task';
  }
  if (tagReminder.status === 'PastDue') {
    description = tagReminder.dueDate
      ? `Due ${moment(tagReminder.dueDate).format('ll')}`
      : 'Reminder date unavailable';
  }
  if (tagReminder.status === 'ScheduleMiss') {
    description = 'Scheduled after the reminder date';
  }
  if (tagReminder.status === 'Scheduled') {
    const nextDate = tagReminder.nextSchedule && tagReminder.nextSchedule.nextOccurrenceDate;
    description = nextDate
      ? `Scheduled ${moment(nextDate).format('ll')}`
      : 'Schedule date unavailable';
  }
  const alignment = center
    ? 'center'
    : 'flex-end';
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: alignment,
        gap: 0.5,
      }}
    >
      {getChipForStatus(tagReminder)}
      <Typography variant="caption" color="text.secondary">{description}</Typography>
    </Box>
  );
};

const TagReminderComponent = ({
  includeHorse,
  tagReminder,
  fullScreen,
  includeTask,
  condensed,
  zIndex,
  triggerDataRefresh,
}) => {
  const [showModal, setShowModal] = React.useState(false);
  const barnHorses = useSelector((state) => state.barn.allBarnHorses || []);
  const barnHorse = barnHorses.find((horse) => Number(horse.pingumTagId) === Number(tagReminder.tag.id));
  const template = tagReminder.eventTemplateReminder.eventTemplate;
  const schedule = tagReminder.nextSchedule;
  const nextDate = schedule && schedule.nextOccurrenceDate;
  const { lastEvent } = tagReminder;
  const horseWidth = fullScreen
    ? 125
    : 210;
  const horseDirection = fullScreen
    ? 'column'
    : 'row';
  const horseTextWidth = fullScreen
    ? 105
    : 150;
  let lastDate = 'No previous tasks';
  let lastLabel = '';
  let lastIcon = <DoNotDisturb color="secondary" />;
  if (lastEvent) {
    lastDate = moment(lastEvent.completeDate || lastEvent.startDate).format('ll');
    lastLabel = lastEvent.completeDate
      ? 'Last Completion'
      : 'Incomplete Since';
    lastIcon = lastEvent.completeDate
      ? <CheckBox color="secondary" />
      : <CheckBoxOutlineBlank color="secondary" />;
  }
  const nextTask = nextDate
    ? moment(nextDate).format('ll')
    : 'Next task not scheduled';
  const nextIcon = nextDate
    ? <Schedule color="secondary" />
    : <DoNotDisturb color="secondary" />;
  let nextDescription = '';
  if (schedule && nextDate) {
    nextDescription = getScheduleSeriesText(schedule) || '';
    if (schedule.endDate) {
      nextDescription = `${nextDescription} Ends ${moment(schedule.endDate).format('ll')}`.trim();
    }
  }
  const dueDate = tagReminder.dueDate
    ? moment(tagReminder.dueDate).format('ll')
    : 'No reminder date';
  let horseContent = (
    <Typography variant="body2">{tagReminder.tag.name || 'Horse details unavailable'}</Typography>
  );
  if (barnHorse) {
    horseContent = (
      <Horse
        size={36}
        horse={barnHorse.horse}
        hideShowName
        flexDirection={horseDirection}
        textWidth={horseTextWidth}
      />
    );
  }
  const taskContent = (
    <ListItem dense disableGutters>
      <ListItemAvatar>
        <Avatar sx={{ backgroundColor: (template.eventDataDefaultValues && template.eventDataDefaultValues.calendarDisplayColor) || primary.main }}>
          <Task sx={{ color: '#fff' }} />
        </Avatar>
      </ListItemAvatar>
      <ListItemText primary={template.name} secondary={`Every ${tagReminder.eventTemplateReminder.reminderDays} days`} />
    </ListItem>
  );
  const openReminder = () => setShowModal(true);
  const handleKeyDown = (event) => {
    if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      openReminder();
    }
  };
  const rowProps = {
    onClick: openReminder,
    onKeyDown: handleKeyDown,
    tabIndex: 0,
    'aria-label': `View ${template.name} reminder for ${tagReminder.tag.name}`,
    sx: { cursor: 'pointer' },
    hover: true,
  };
  let reminderRow = (
    <TableRow {...rowProps}>
      {includeHorse && (
        <TableCell
          sx={{
            ...cellStyle,
            width: horseWidth,
            minWidth: horseWidth,
          }}
        >
          {horseContent}
        </TableCell>
      )}
      {includeTask && <TableCell sx={cellStyle}>{taskContent}</TableCell>}
      <TableCell sx={cellStyle}>{getChipForStatus(tagReminder)}</TableCell>
      <TableCell sx={cellStyle}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <AccessAlarm color="secondary" />
          <Tooltip title="The reminder date is based on the most recent completed task."><Box>{dueDate}</Box></Tooltip>
        </Box>
      </TableCell>
      <TableCell sx={cellStyle}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {lastIcon}
          <Box>
            <Typography variant="caption" component="div">{lastLabel}</Typography>
            {lastDate}
          </Box>
        </Box>
      </TableCell>
      <TableCell sx={cellStyle}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {nextIcon}
          <Tooltip title={nextDescription}><Box>{nextTask}</Box></Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );
  if (condensed) {
    reminderRow = (
      <TableRow {...rowProps}>
        <TableCell>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            {includeHorse && horseContent}
            {includeTask && <Box sx={{ flex: 1 }}>{taskContent}</Box>}
            <TagReminderStatus tagReminder={tagReminder} />
          </Box>
        </TableCell>
      </TableRow>
    );
  }
  return (
    <>
      {reminderRow}
      {showModal && (
        <TagReminderModal
          tagReminder={tagReminder}
          open
          onClose={() => setShowModal(false)}
          triggerDataRefresh={triggerDataRefresh}
          zIndex={zIndex}
        />
      )}
    </>
  );
};

TagReminderComponent.propTypes = {
  tagReminder: PropTypes.object.isRequired,
  fullScreen: PropTypes.bool,
  includeHorse: PropTypes.bool,
  includeTask: PropTypes.bool,
  condensed: PropTypes.bool,
  zIndex: PropTypes.number,
  triggerDataRefresh: PropTypes.func,
};

TagReminderComponent.defaultProps = {
  fullScreen: false,
  includeHorse: true,
  includeTask: false,
  condensed: false,
  zIndex: undefined,
  triggerDataRefresh: () => {},
};

export default TagReminderComponent;
